import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, FormHelperText, Typography } from '@mui/material';
import FormPage from '../components/FormPage';
import { updateCustomer, fetchCustomers } from '../services/ApiService';

const FIELDS = [
  { label: "Customer", name: "name", required: true, placeholder: "Company or client name" },
  { label: "Contact Person", name: "contact_person", placeholder: "Primary contact's name" },
  { label: "Email", name: "email", required: true, placeholder: "name@company.com" },
  { label: "Contact Number", name: "contact_number", type: "tel", placeholder: "+92 300 1234567" },
  { label: "Business Type", name: "business_type", placeholder: "e.g. Fleet Operator, OMC, Logistics" },
  { label: "Corporate", name: "corporate", placeholder: "e.g. Wafi / UEP / TPPL" },
  { label: "Plan", name: "plan", placeholder: "e.g. AFDD, Dashcam, MDVR" },
  { label: "Contract Expiry Date", name: "contract_expiry_date", type: "date" },
  { label: "Billing Contact", name: "billing_contact", placeholder: "Name & contact of billing POC" },
  { label: "Region/City", name: "region_city", placeholder: "Main operational area" },
  { label: "Remarks", name: "remarks", placeholder: "Any additional notes" },
];

export function EditCustomerForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [formData, setFormData] = React.useState(location.state?.record || null);
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    if (!formData) {
      fetchCustomers().then((res) => {
        const found = res.data.find((c) => String(c.id) === String(id));
        if (found) setFormData(found);
        else navigate('/add-customer');
      }).catch(() => navigate('/add-customer'));
    }
  }, [formData, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCancel = () => navigate('/add-customer');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    try {
      await updateCustomer(id, formData);
      navigate('/add-customer', { state: { successMessage: 'Customer updated successfully' } });
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.errors) setFormErrors(errorData.errors);
      else setFormErrors({ general: errorData?.message || 'Failed to update customer.' });
    }
  };

  if (!formData) {
    return (
      <FormPage title="Edit Customer" backTo="/add-customer">
        <Typography>Loading...</Typography>
      </FormPage>
    );
  }

  return (
    <FormPage
      title="Edit Customer"
      backTo="/add-customer"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Update</Button>
      </>}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.75rem" }}>
        Last updated: {formData.updated_at?.slice(0, 19).replace("T", " ")}
      </Typography>

      <Grid container spacing={2}>
        {FIELDS.map((field) => (
          <Grid item xs={12} sm={6} key={field.name}>
            <TextField
              fullWidth label={field.label} name={field.name} placeholder={field.placeholder}
              type={field.type || "text"} value={formData[field.name] || ""}
              onChange={handleChange} required={field.required || false}
              error={!!formErrors[field.name]} helperText={formErrors[field.name]}
              InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!formErrors.billing_cycle}>
            <InputLabel>Billing Cycle</InputLabel>
            <Select label="Billing Cycle" name="billing_cycle" value={formData.billing_cycle || ""} onChange={handleChange} displayEmpty>
              <MenuItem value="" disabled>Select billing cycle</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Annual">Annual</MenuItem>
            </Select>
            {formErrors.billing_cycle && <FormHelperText>{formErrors.billing_cycle}</FormHelperText>}
          </FormControl>
        </Grid>

        {formErrors.general && (
          <Grid item xs={12}>
            <FormHelperText error>{formErrors.general}</FormHelperText>
          </Grid>
        )}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
        Created: {formData.created_at?.slice(0, 19).replace("T", " ")}
      </Typography>
    </FormPage>
  );
}
